/** @jsxRuntime classic */
/** @jsx jsx */
import { memo, useEffect, forwardRef, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import Alert from "./";
import theme from "../../theme/settings";
import { classNames } from "../../utils";
import { bgTransparent, pointerEventsNone, positionFixed } from "../../styles/general";
const {
  zIndex: zIndexCORE
} = theme;
const positionVerticalMap = new Map([['top', css`
            top: 15px;
        `], ['center', css`
            top: 50%;
            transform: translateY(-50%);
        `], ['bottom', css`
            display: flex;
            flex-direction: column-reverse;
            bottom: 0;
        `]]);
const positionHorizontalMap = new Map([['left', css`
            left: 15px;
        `], ['center', css`
            left: 50%;
            transform: translateX(-50%);
        `], ['right', css`
            right: 15px;
        `]]);
const Notify = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  className,
  getAddNotify,
  id,
  position,
  style,
  ...props
}, reference) => {
  const ref = useRef(null);
  const [positionState, setPositionState] = useState(position);
  const PositionVerticalCSS = positionVerticalMap.get((positionState === null || positionState === void 0 ? void 0 : positionState.vertical) || 'bottom');
  const PositionHorizontalCSS = positionHorizontalMap.get((positionState === null || positionState === void 0 ? void 0 : positionState.horizontal) || 'center');
  const showNotify = (message, {
    position,
    ...options
  } = {}) => {
    options = typeof options === 'object' ? options : {};
    if (position) {
      setPositionState(position);
    }
    const el = document.createElement('div');
    el.className = 'DGN-UI-Notify-Alert-Item';
    ReactDOM.render(jsx(Alert
    // onClose={() => el.remove()}
    , {
      secondary: message,
      ...props,
      ...options
    }), ref.current.appendChild(el));
  };
  useEffect(() => {
    if (getAddNotify && typeof getAddNotify === 'function') getAddNotify(showNotify);
  }, [props]);
  useEffect(() => {
    setPositionState(position);
  }, [position]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      show: showNotify,
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    currentRef.add = showNotify;
    return currentRef;
  });
  const NotifyContainerView = jsx("div", {
    ref: ref,
    css: [AlertCSS, PositionVerticalCSS, PositionHorizontalCSS],
    id: id,
    style: style,
    className: classNames('DGN-UI-Notify-Alert', (position === null || position === void 0 ? void 0 : position.vertical) || 'center' + '-' + (position === null || position === void 0 ? void 0 : position.horizontal) || 'center', className)
  });
  return /*#__PURE__*/createPortal(NotifyContainerView, document.body);
}));
const AlertCSS = css`
    ${positionFixed};
    ${pointerEventsNone};
    ${bgTransparent};
    z-index: ${zIndexCORE(2)};
`;
Notify.defaultProps = {
  className: '',
  position: {
    vertical: 'bottom',
    horizontal: 'center'
  },
  style: {}
};
Notify.propTypes = {
  /** Class for component. */
  className: PropTypes.string,
  /**
   * used to set position of Alert (is a string or an object)
   * string is center (obligatory) as {vertical: 'center', horizontal: 'center'}
   */
  position: PropTypes.oneOfType([PropTypes.oneOf(['center']), PropTypes.shape({
    horizontal: PropTypes.oneOf(['center', 'left', 'right']),
    vertical: PropTypes.oneOf(['bottom', 'center', 'top'])
  })]),
  /** Style inline of component. */
  style: PropTypes.object,
  /** Any [props](https://core.diginet.com.vn/ui/?path=/docs/alert--basic) of Alert */
  props: PropTypes.any
  /**
   * ref methods (ref.current.instance.*method*)
   *
   * * show(message, option): Show notify
   *  * @param {message} - number
   *  * @param {options} - object
   *      * {[Props](https://core.diginet.com.vn/ui/?path=/docs/alert--basic) applied to the `Alert`.}
   */
};

export default Notify;